import { useCallback, useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  CheckCircleOutline,
  LockOutlined,
  MailOutline,
  PeopleAltOutlined,
  SendOutlined,
} from "@mui/icons-material";

import {
  approveParentStudentLink,
  fetchParentStudentLinks,
  rejectParentStudentLink,
  sendParentStudentLinkRequest,
} from "../../services/api";
import { useToast } from "../../hook/useToast";

const RELATIONSHIP_OPTIONS = [
  { value: "father", label: "Father" },
  { value: "mother", label: "Mother" },
  { value: "guardian", label: "Guardian" },
];

const styles = {
  shell: {
    width: "100%",
    maxWidth: 1120,
    mx: "auto",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    gap: 2,
    alignItems: { xs: "flex-start", md: "center" },
    flexDirection: { xs: "column", md: "row" },
    mb: 2.5,
  },
  title: {
    fontSize: { xs: 22, md: 26 },
    fontWeight: 800,
    color: "#24284F",
  },
  subtitle: {
    color: "#687086",
    fontSize: 14,
    mt: 0.5,
    maxWidth: 620,
  },
  formCard: {
    p: { xs: 2, md: 2.5 },
    borderRadius: "8px",
    border: "1px solid #E7EAF2",
    bgcolor: "#fff",
    boxShadow: "0 12px 28px rgba(31, 38, 79, 0.06)",
    mb: 3,
  },
  sectionCard: {
    p: { xs: 2, md: 2.5 },
    borderRadius: "8px",
    border: "1px solid #E7EAF2",
    bgcolor: "#fff",
    boxShadow: "0 10px 24px rgba(31, 38, 79, 0.05)",
  },
  sectionTitle: {
    fontSize: 16,
    fontWeight: 800,
    color: "#24284F",
  },
  muted: {
    color: "#6F778A",
    fontSize: 13,
  },
  linkCard: {
    p: 2,
    borderRadius: "8px",
    border: "1px solid #EEF0F5",
    bgcolor: "#FAFBFD",
  },
};

const getResponseLinks = (response) =>
  response?.data?.links || response?.data?.data?.links || [];

const getResponseLink = (response) =>
  response?.data?.link || response?.data?.data?.link || null;

const getErrorMessage = (error) => {
  const data = error?.response?.data;
  const errors = data?.errors || data;

  if (typeof errors === "string") return errors;
  if (Array.isArray(errors?.detail)) return errors.detail[0];
  if (typeof errors?.detail === "string") return errors.detail;

  if (errors && typeof errors === "object") {
    const firstError = Object.values(errors).find(Array.isArray)?.[0];
    if (firstError) return firstError;
  }

  return data?.message || "Something went wrong. Please try again.";
};

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const getStatusChip = (link) => {
  if (link.status === "accepted") {
    return { label: "Linked", color: "#16794C", bg: "#DFF6E9" };
  }
  if (link.status === "pending_final") {
    return { label: "Final confirmation", color: "#8A5A00", bg: "#FFF4D5" };
  }
  if (link.status === "rejected") {
    return { label: "Rejected", color: "#9E1C1C", bg: "#FFE1E1" };
  }
  return { label: "Pending", color: "#294B9B", bg: "#E8EFFF" };
};

const getTargetPerson = (link, mode) =>
  mode === "parent" ? link.student : link.parent;

const getPrimaryLine = (link, mode) => {
  const person = getTargetPerson(link, mode);
  const relationship = link.relationship_label || "Parent";

  if (link.status === "accepted") {
    return mode === "parent"
      ? `${person?.name || "Child"} is linked with you.`
      : `${relationship} is linked with you.`;
  }

  if (link.can_current_user_approve) {
    if (link.status === "pending_final") {
      return "Final confirmation is waiting for you.";
    }
    return mode === "parent"
      ? `${person?.name || "Student"} requested to link with you.`
      : `${relationship} requested access to your portal.`;
  }

  return "Waiting for the other side to approve.";
};

const EmptyState = ({ text }) => (
  <Box
    sx={{
      border: "1px dashed #CDD3E1",
      borderRadius: "8px",
      p: 2.5,
      textAlign: "center",
      color: "#7B8498",
      bgcolor: "#FBFCFE",
      fontSize: 14,
    }}
  >
    {text}
  </Box>
);

EmptyState.propTypes = {
  text: PropTypes.string.isRequired,
};

const LinkCard = ({ link, mode, onApprove, onReject, actionLoading }) => {
  const person = getTargetPerson(link, mode);
  const statusChip = getStatusChip(link);
  const isLocked = link.status === "accepted";
  const canAct = Boolean(link.can_current_user_approve);
  const approveLabel =
    link.status === "pending_final" ? "Final Confirm" : "Accept";

  return (
    <Paper elevation={0} sx={styles.linkCard}>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        gap={2}
      >
        <Box>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#24284F" }}>
              {person?.name || "User"}
            </Typography>
            <Chip
              size="small"
              label={link.relationship_label || "Parent"}
              sx={{ bgcolor: "#F2F4F8", color: "#3C4355", fontWeight: 700 }}
            />
            <Chip
              size="small"
              label={statusChip.label}
              sx={{
                bgcolor: statusChip.bg,
                color: statusChip.color,
                fontWeight: 800,
              }}
            />
            {isLocked && (
              <Chip
                size="small"
                icon={<LockOutlined sx={{ fontSize: "15px !important" }} />}
                label="Locked"
                sx={{ bgcolor: "#EEF0F5", color: "#485066", fontWeight: 700 }}
              />
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={0.8} mt={1}>
            <MailOutline sx={{ fontSize: 16, color: "#7A8398" }} />
            <Typography sx={styles.muted}>{person?.email || "-"}</Typography>
          </Stack>

          {mode === "parent" && person?.school && (
            <Typography sx={{ ...styles.muted, mt: 0.5 }}>
              {person.school}
              {person.student_class ? `, Class ${person.student_class}` : ""}
              {person.section ? `-${person.section}` : ""}
            </Typography>
          )}

          <Typography sx={{ color: "#4B556B", fontSize: 13.5, mt: 1.2 }}>
            {getPrimaryLine(link, mode)}
          </Typography>

          <Typography sx={{ color: "#9AA2B5", fontSize: 12, mt: 0.8 }}>
            {isLocked
              ? `Linked ${formatDate(link.accepted_at) || ""}`
              : `Requested ${formatDate(link.created_at) || ""}`}
          </Typography>
        </Box>

        {canAct && !isLocked && (
          <Stack direction="row" spacing={1} alignItems="center">
            <Button
              variant="contained"
              disabled={actionLoading}
              onClick={() => onApprove(link.id)}
              startIcon={<CheckCircleOutline />}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                bgcolor: "#2D315F",
                "&:hover": { bgcolor: "#252957" },
              }}
            >
              {approveLabel}
            </Button>
            <Button
              variant="outlined"
              disabled={actionLoading}
              onClick={() => onReject(link.id)}
              sx={{
                textTransform: "none",
                borderRadius: "8px",
                borderColor: "#E5A4A4",
                color: "#9E1C1C",
              }}
            >
              Reject
            </Button>
          </Stack>
        )}
      </Stack>
    </Paper>
  );
};

LinkCard.propTypes = {
  link: PropTypes.shape({
    id: PropTypes.number.isRequired,
    accepted_at: PropTypes.string,
    can_current_user_approve: PropTypes.bool,
    created_at: PropTypes.string,
    parent: PropTypes.object,
    relationship_label: PropTypes.string,
    status: PropTypes.string,
    student: PropTypes.object,
  }).isRequired,
  mode: PropTypes.oneOf(["student", "parent"]).isRequired,
  onApprove: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  actionLoading: PropTypes.bool.isRequired,
};

const ParentAccessSection = ({ mode }) => {
  const { showToast } = useToast();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [targetEmail, setTargetEmail] = useState("");
  const [relationship, setRelationship] = useState("father");

  const isParentMode = mode === "parent";
  const labels = useMemo(
    () => ({
      title: isParentMode ? "Children Access" : "Parent Access",
      subtitle: isParentMode
        ? "Send requests to children, approve child requests, and view linked children."
        : "Connect your father, mother, or guardian with a two-step approval flow.",
      sendTitle: isParentMode ? "Send Request To Child" : "Send Request To Parent",
      emailLabel: isParentMode ? "Child email address" : "Parent email address",
      linkedTitle: isParentMode ? "Linked Children" : "Linked Parents",
      pendingTitle: "Needs Your Approval",
      waitingTitle: "Waiting For Other Side",
      emptyLinked: isParentMode
        ? "No children are linked yet."
        : "No parents are linked yet.",
    }),
    [isParentMode],
  );

  const loadLinks = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetchParentStudentLinks();
      setLinks(getResponseLinks(response));
    } catch (error) {
      showToast(getErrorMessage(error), "error");
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => {
    loadLinks();
  }, [loadLinks]);

  const groupedLinks = useMemo(() => {
    const activeLinks = links.filter((link) => link.status === "accepted");
    const needsApproval = links.filter(
      (link) =>
        ["pending_recipient", "pending_final"].includes(link.status) &&
        link.can_current_user_approve,
    );
    const waiting = links.filter(
      (link) =>
        ["pending_recipient", "pending_final"].includes(link.status) &&
        !link.can_current_user_approve,
    );
    return { activeLinks, needsApproval, waiting };
  }, [links]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!targetEmail.trim()) {
      showToast("Email address is required", "error");
      return;
    }

    setSubmitting(true);
    try {
      const response = await sendParentStudentLinkRequest({
        targetEmail: targetEmail.trim(),
        relationship,
        requesterRole: mode,
      });
      const createdLink = getResponseLink(response);
      if (createdLink) {
        setLinks((currentLinks) => [createdLink, ...currentLinks]);
      } else {
        await loadLinks();
      }
      setTargetEmail("");
      showToast("Request sent successfully", "success");
    } catch (error) {
      showToast(getErrorMessage(error), "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (linkId) => {
    setActionLoading(true);
    try {
      const response = await approveParentStudentLink(linkId);
      const updatedLink = getResponseLink(response);
      setLinks((currentLinks) =>
        currentLinks.map((link) => (link.id === linkId ? updatedLink || link : link)),
      );
      showToast(response?.data?.message || "Request approved", "success");
    } catch (error) {
      showToast(getErrorMessage(error), "error");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (linkId) => {
    setActionLoading(true);
    try {
      const response = await rejectParentStudentLink(linkId);
      const updatedLink = getResponseLink(response);
      setLinks((currentLinks) =>
        currentLinks.map((link) => (link.id === linkId ? updatedLink || link : link)),
      );
      showToast(response?.data?.message || "Request rejected", "success");
    } catch (error) {
      showToast(getErrorMessage(error), "error");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box sx={styles.shell}>
      <Box sx={styles.header}>
        <Box>
          <Stack direction="row" spacing={1.2} alignItems="center">
            <PeopleAltOutlined sx={{ color: "#F6AA48", fontSize: 30 }} />
            <Typography sx={styles.title}>{labels.title}</Typography>
          </Stack>
          <Typography sx={styles.subtitle}>{labels.subtitle}</Typography>
        </Box>
        <Chip
          icon={<LockOutlined sx={{ fontSize: "16px !important" }} />}
          label="Accepted links are locked"
          sx={{ bgcolor: "#EEF1F8", color: "#3D4560", fontWeight: 700 }}
        />
      </Box>

      <Paper component="form" elevation={0} sx={styles.formCard} onSubmit={handleSubmit}>
        <Typography sx={{ ...styles.sectionTitle, mb: 1.5 }}>
          {labels.sendTitle}
        </Typography>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
          <TextField
            label={labels.emailLabel}
            value={targetEmail}
            onChange={(event) => setTargetEmail(event.target.value)}
            type="email"
            fullWidth
            size="small"
          />
          <FormControl size="small" sx={{ minWidth: { xs: "100%", md: 180 } }}>
            <InputLabel>Relationship</InputLabel>
            <Select
              value={relationship}
              label="Relationship"
              onChange={(event) => setRelationship(event.target.value)}
            >
              {RELATIONSHIP_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            endIcon={submitting ? <CircularProgress size={16} /> : <SendOutlined />}
            sx={{
              minWidth: 150,
              textTransform: "none",
              borderRadius: "8px",
              bgcolor: "#F6AA48",
              color: "#fff",
              fontWeight: 800,
              "&:hover": { bgcolor: "#DB9131" },
            }}
          >
            Send Request
          </Button>
        </Stack>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" py={5}>
          <CircularProgress />
        </Box>
      ) : (
        <Stack spacing={2.5}>
          <Paper elevation={0} sx={styles.sectionCard}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography sx={styles.sectionTitle}>{labels.linkedTitle}</Typography>
              <Chip
                size="small"
                label={groupedLinks.activeLinks.length}
                sx={{ bgcolor: "#E8F7EF", color: "#16794C", fontWeight: 800 }}
              />
            </Stack>
            <Stack spacing={1.5}>
              {groupedLinks.activeLinks.length ? (
                groupedLinks.activeLinks.map((link) => (
                  <LinkCard
                    key={link.id}
                    link={link}
                    mode={mode}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    actionLoading={actionLoading}
                  />
                ))
              ) : (
                <EmptyState text={labels.emptyLinked} />
              )}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={styles.sectionCard}>
            <Typography sx={styles.sectionTitle}>{labels.pendingTitle}</Typography>
            <Typography sx={{ ...styles.muted, mb: 1.5 }}>
              Accepting may move the request to final confirmation, depending on who sent it first.
            </Typography>
            <Stack spacing={1.5}>
              {groupedLinks.needsApproval.length ? (
                groupedLinks.needsApproval.map((link) => (
                  <LinkCard
                    key={link.id}
                    link={link}
                    mode={mode}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    actionLoading={actionLoading}
                  />
                ))
              ) : (
                <EmptyState text="No requests need your approval right now." />
              )}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={styles.sectionCard}>
            <Typography sx={styles.sectionTitle}>{labels.waitingTitle}</Typography>
            <Typography sx={{ ...styles.muted, mb: 1.5 }}>
              These requests are pending with the other side.
            </Typography>
            <Stack spacing={1.5}>
              {groupedLinks.waiting.length ? (
                groupedLinks.waiting.map((link) => (
                  <LinkCard
                    key={link.id}
                    link={link}
                    mode={mode}
                    onApprove={handleApprove}
                    onReject={handleReject}
                    actionLoading={actionLoading}
                  />
                ))
              ) : (
                <EmptyState text="No outgoing or waiting requests." />
              )}
            </Stack>
          </Paper>

          <Alert severity="info" sx={{ borderRadius: "8px" }}>
            A parent can link with multiple children. A student can link with multiple parents.
            Once accepted, the link is locked.
          </Alert>
        </Stack>
      )}
    </Box>
  );
};

ParentAccessSection.propTypes = {
  mode: PropTypes.oneOf(["student", "parent"]).isRequired,
};

export default ParentAccessSection;
